import React, { useState } from 'react';
import { ChevronUp, ChevronDown, MessageCircle, Mail, Phone } from 'lucide-react';
import '../../../styles/pages/help-support.css';

const FAQ_ITEMS = [
  {
    q: "What does 'Air Quality' refer to?",
    a: "Air quality refers to the condition of the air in our environment, specifically the presence of pollutants that can impact human health and the environment.",
  },
  {
    q: 'What are common air pollutants present in the environment?',
    a: "Common air pollutants include Particulate Matter (PM2.5 and PM10), Nitrogen Dioxide (NO₂), Sulfur Dioxide (SO₂), Carbon Monoxide (CO), Ozone (O₃), and Volatile Organic Compounds (VOCs). These pollutants come from vehicle emissions, industrial activities, construction, and natural sources such as dust storms.",
  },
  {
    q: 'How does air quality influence weather patterns?',
    a: "Air quality and weather are closely linked. Pollutants like aerosols and particulate matter can affect cloud formation, reduce visibility, and alter precipitation patterns. Poor air quality can trap heat near the surface, contributing to temperature inversions that further worsen pollution levels, particularly in urban areas.",
  },
  {
    q: 'What is the number of air quality monitoring stations currently operational in Abu Dhabi?',
    a: "Abu Dhabi operates a network of over 20 air quality monitoring stations managed by the Environment Agency – Abu Dhabi (EAD). These stations are strategically placed across urban, industrial, and residential areas to continuously track pollutant levels and ensure compliance with national and international air quality standards.",
  },
  {
    q: 'Who is considered in a sensitive group?',
    a: "Sensitive groups include children, the elderly, pregnant women, and individuals with pre-existing respiratory or cardiovascular conditions such as asthma, bronchitis, or heart disease. These groups are more vulnerable to the health effects of air pollution and should take extra precautions when air quality levels are moderate to unhealthy.",
  },
  {
    q: 'Does air quality play a role in climate change?',
    a: "Yes. Many air pollutants are also greenhouse gases or interact with them. Short-lived climate pollutants such as black carbon, methane, and tropospheric ozone contribute directly to global warming. Improving air quality by reducing these emissions can have both immediate health benefits and long-term positive effects on climate change.",
  },
  {
    q: 'How can individuals contribute to reducing air pollution?',
    a: "Individuals can help by using public transportation, carpooling, or switching to electric vehicles; reducing energy consumption at home; avoiding burning waste or using open fires; choosing environmentally friendly products; and supporting policies that promote clean energy. Small everyday choices collectively make a significant difference in improving air quality.",
  },
  {
    q: 'How do I reset my password?',
    a: 'Go to the login page and click "Forgot Password". You will receive an email with reset instructions. Follow the link in the email to set a new password. If you do not receive the email within a few minutes, please check your spam folder or contact support.',
  },
  {
    q: 'How do I add a new device?',
    a: 'Navigate to Devices > Add Device and follow the on-screen setup wizard. You will need to provide the device name, location, and sensor type. Once registered, the device will begin reporting data to the dashboard within a few minutes.',
  },
  {
    q: 'Can I export my data?',
    a: 'Yes. Open any report page and click the Export button. CSV and Excel formats are supported. You can export data for a custom date range and apply filters before exporting to get exactly the data you need.',
  },
  {
    q: 'How do I invite team members?',
    a: 'Go to User Management > Add New User and enter their email address and assigned role. They will receive an invitation email with instructions to set up their account. You can manage or revoke access at any time from the User Management page.',
  },
  {
    q: 'What browsers are supported?',
    a: 'We support Chrome, Firefox, Safari, and Edge (latest two versions each). For the best experience, we recommend using Google Chrome or Microsoft Edge. Older browser versions may not support all dashboard features.',
  },
];

interface FAQTabProps {
  GoToChatSupport: () => void;
}

const FAQTab: React.FC<FAQTabProps> = ({ GoToChatSupport }) => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(0);

  return (
    <div className="hs-faq-layout">
      {/* Left: FAQ accordion */}
      <div className="hs-faq-section">
        <p className="na-section-label">FREQUENTLY ASKED QUESTIONS</p>
        <div className='hs-faq-list-wrapper scrollbar'>
          <div className="hs-faq-list">
            {FAQ_ITEMS.map((item, i) => (
              <div key={i} className="hs-faq-item">
                <button
                  className={`hs-faq-question ${expandedFaq === i ? 'expanded' : ''}`}
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                >
                  {item.q}
                  <span className="hs-faq-toggle">
                    {expandedFaq === i ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </span>
                </button>
                {expandedFaq === i && item.a && (
                  <p className="hs-faq-answer">{item.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Still need help? */}
      <div className="hs-faq-sidebar">
        <p className="na-section-label">STILL NEED HELP?</p>
        <button className="hs-faq-chat-btn" onClick={GoToChatSupport}>
          <MessageCircle size={16} />
          Start Chat Support
        </button>
        <a className="hs-faq-contact-btn" href='mailto:supportsmarteco@ispatialtec.com'>
          <Mail size={15} />
          Email: supportsmarteco@ispatialtec.com
        </a>
        <button className="hs-faq-contact-btn">
          <Phone size={15} />
          Call: +971 2 000 0000
        </button>
      </div>
    </div>
  );
};

export default FAQTab;
